import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { MenubarModule } from 'primeng/menubar';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { MenuItem } from 'primeng/api';
import { NavItem } from '@fy-web-2/shared-util';

@Component({
  selector: 'lib-header',
  standalone: true,
  imports: [ButtonModule, AvatarModule, MenubarModule, TieredMenuModule],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  /**
   * Nested nav tree provided by the App shell (typically derived from
   * `NavigationFacade.navItems()`). Adapted to PrimeNG's `MenuItem[]` shape
   * inside the component to keep the shell's data flow purely typed.
   */
  readonly items = input<readonly NavItem[]>([]);

  /**
   * Permission-filtered tree backing the right-side `+ New` action menu
   * (Rates ▸ Ratecard / Ratesheet → Quote → Booking). When empty the button
   * hides itself.
   */
  readonly createItems = input<readonly NavItem[]>([]);

  /**
   * Tenant logo URL fetched from `domain-data` and surfaced through
   * `TenantFacade.logoUrl()`. When `null`, the header falls back to the
   * built-in Freightify "F" badge.
   */
  readonly logoUrl = input<string | null>(null);

  /**
   * Tenant display name (`domain_title`). When `null`, "Freightify" is used.
   */
  readonly brandName = input<string | null>(null);

  readonly logout = output<void>();

  /** Emitted when the `Upload Rate File` outlined button is clicked. */
  readonly uploadRateFile = output<void>();

  protected readonly menuItems = computed<MenuItem[]>(() => toMenuItems(this.items()));
  protected readonly createMenuItems = computed<MenuItem[]>(() => toMenuItems(this.createItems()));
  protected readonly hasCreateMenu = computed(() => this.createMenuItems().length > 0);

  protected onLogoutClick(): void {
    this.logout.emit();
  }

  protected onUploadRateFileClick(): void {
    this.uploadRateFile.emit();
  }
}

/**
 * Adapts our internal `NavItem` tree to PrimeNG's `MenuItem[]`. We deliberately
 * omit `icon` so menus render text-only labels — matching the legacy
 * `fb-shipper-frontend` look (only group items get the auto-injected chevron).
 */
function toMenuItems(nodes: readonly NavItem[]): MenuItem[] {
  return nodes.map((n) => {
    const hasChildren = !!n.children?.length;
    const item: MenuItem = { label: n.label };
    if (hasChildren) {
      item.items = toMenuItems(n.children ?? []);
    } else if (n.route) {
      item.routerLink = n.route;
    }
    return item;
  });
}
